import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

interface CompaniesHouseStatus {
  success: boolean;
  configured: boolean;
  connected?: boolean;
  message: string;
  setup_instructions?: {
    step_1: string;
    step_2: string;
    step_3: string;
    step_4: string;
  };
  test_result?: {
    company_number: string;
    company_name: string;
    company_status: string;
  };
  rate_limit_info?: {
    requests_made: number;
    window_start: string;
    limit: string;
  };
}

interface CompanyProfile {
  company_number: string;
  company_name: string;
  company_status: string;
  company_type: string;
  incorporation_date?: string;
  dissolution_date?: string;
  registered_office_address: {
    address_line_1?: string;
    address_line_2?: string;
    locality?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  sic_codes: string[];
  accounts?: any;
  confirmation_statement?: any;
}

const CompaniesHouseIntegration: React.FC = () => {
  const [status, setStatus] = useState<CompaniesHouseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [companyNumber, setCompanyNumber] = useState('');
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Streaming state
  const [streamingStatus, setStreamingStatus] = useState<any>(null);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [monitoredCompanies, setMonitoredCompanies] = useState<string[]>([]);
  const [streamingEvents, setStreamingEvents] = useState<any[]>([]);

  // OAuth2 state
  const [oauthStatus, setOauthStatus] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);

  useEffect(() => {
    checkIntegrationStatus();
    checkStreamingStatus();
    checkOAuthStatus();
  }, []);

  const checkIntegrationStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/companies/companies-house/status');
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError('Failed to check Secretary of State integration status');
      console.error('Status check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const lookupCompany = async () => {
    if (!companyNumber.trim()) {
      setError('Please enter a company number');
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      setCompanyProfile(null);

      const response = await fetch(`/api/v1/companies/lookup/${companyNumber.trim()}`);
      const data = await response.json();

      if (data.success) {
        setCompanyProfile(data.data);
      } else {
        setError(data.message || 'Company lookup failed');
      }
    } catch (err) {
      setError('Failed to lookup company information');
      console.error('Company lookup error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const validateCompany = async () => {
    if (!companyNumber.trim()) {
      setError('Please enter a company number');
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);

      const response = await fetch(`/api/v1/companies/validate/${companyNumber.trim()}`);
      const data = await response.json();

      if (data.success) {
        setError(null);
        alert(`✅ Valid company: ${data.data.company_name} (${data.data.company_status})`);
      } else {
        setError(data.message || 'Company validation failed');
      }
    } catch (err) {
      setError('Failed to validate company number');
      console.error('Company validation error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Streaming functions
  const checkStreamingStatus = async () => {
    try {
      const response = await fetch('/api/v1/companies/streaming/status');
      const data = await response.json();

      if (data.success) {
        setStreamingStatus(data.data);
      }
    } catch (err) {
      console.error('Failed to check streaming status:', err);
    }
  };

  const createStreamingSubscription = async () => {
    if (!companyNumber.trim()) {
      setError('Please enter a company number to monitor');
      return;
    }

    try {
      setSearchLoading(true);
      const response = await fetch(
        `/api/v1/companies/streaming/subscribe?company_numbers=${companyNumber.trim()}&event_types=changed&event_types=deleted&event_types=created`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setMonitoredCompanies([...monitoredCompanies, companyNumber.trim()]);
        setSubscriptions([...subscriptions, data.data]);
        alert(`✅ Now monitoring company ${companyNumber.trim()} for real-time changes`);
        setCompanyNumber('');
      } else {
        setError(data.message || 'Failed to create streaming subscription');
      }
    } catch (err) {
      setError('Failed to create streaming subscription');
      console.error('Streaming subscription error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // OAuth2 functions
  const checkOAuthStatus = async () => {
    try {
      const response = await fetch('/api/v1/companies/oauth/status');
      const data = await response.json();

      if (data.success) {
        setOauthStatus(data.data);
      }
    } catch (err) {
      console.error('Failed to check OAuth status:', err);
    }
  };

  const initiateOAuthFlow = async () => {
    try {
      setSearchLoading(true);
      const response = await fetch(
        '/api/v1/companies/oauth/authorize?user_id=frontend_user',
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setAuthorizationUrl(data.data.authorization_url);
        // Open authorization URL in new window
        window.open(data.data.authorization_url, '_blank', 'width=600,height=700');
        alert('🔐 Authorization window opened. Please complete the OAuth flow and return here.');
      } else {
        setError(data.message || 'Failed to initiate OAuth flow');
      }
    } catch (err) {
      setError('Failed to initiate OAuth flow');
      console.error('OAuth initiation error:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!status) return <InfoIcon />;
    if (status.success && status.connected) return <CheckCircleIcon color="success" />;
    if (status.configured && !status.connected) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const getStatusColor = () => {
    if (!status) return 'info';
    if (status.success && status.connected) return 'success';
    if (status.configured && !status.connected) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 2 }}>
      <Typography variant="h4" gutterBottom>
        Secretary of State Integration
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Real-time company lookup and validation using the official Secretary of State API
      </Typography>

      {/* Integration Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            {getStatusIcon()}
            <Typography variant="h6">Integration Status</Typography>
            <Chip
              label={status?.success && status?.connected ? 'Connected' : status?.configured ? 'Configured' : 'Not Configured'}
              color={getStatusColor() as any}
              size="small"
            />
          </Box>

          <Alert severity={getStatusColor() as any} sx={{ mb: 2 }}>
            {status?.message}
          </Alert>

          {status?.setup_instructions && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Setup Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Step 1"
                      secondary={status.setup_instructions.step_1}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Step 2"
                      secondary={status.setup_instructions.step_2}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Step 3"
                      secondary={status.setup_instructions.step_3}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Step 4"
                      secondary={status.setup_instructions.step_4}
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {status?.test_result && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="subtitle2" gutterBottom>Test Connection Result:</Typography>
              <Typography variant="body2">
                {status.test_result.company_name} ({status.test_result.company_number}) - {status.test_result.company_status}
              </Typography>
            </Paper>
          )}

          {status?.rate_limit_info && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" display="block">
                Rate Limit: {status.rate_limit_info.limit}
              </Typography>
              <Typography variant="caption" display="block">
                Requests Made: {status.rate_limit_info.requests_made}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Company Lookup */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Company Lookup & Validation
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Number"
                placeholder="e.g., 12345678 or SC123456"
                value={companyNumber}
                onChange={(e) => setCompanyNumber(e.target.value)}
                helperText="Enter 8-digit company number or 2 letters + 6 digits"
                disabled={!status?.configured}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={validateCompany}
                  disabled={!status?.configured || searchLoading}
                >
                  Validate
                </Button>
                <Button
                  variant="contained"
                  startIcon={<BusinessIcon />}
                  onClick={lookupCompany}
                  disabled={!status?.configured || searchLoading}
                >
                  {searchLoading ? <CircularProgress size={20} /> : 'Lookup'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Streaming Section */}
          {streamingStatus && (
            <Paper sx={{ mt: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                🔄 Real-time Monitoring
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Monitor companies for real-time changes including status updates,
                    filing notifications, and officer changes.
                  </Typography>

                  {streamingStatus.streaming_configured ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      ✅ Streaming API configured and ready
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      ⚠️ Streaming API not configured
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={createStreamingSubscription}
                    disabled={searchLoading || !streamingStatus.streaming_configured}
                    startIcon={<NotificationsIcon />}
                    fullWidth
                  >
                    Monitor This Company
                  </Button>
                </Grid>
              </Grid>

              {monitoredCompanies.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Currently Monitoring:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {monitoredCompanies.map((companyNum, index) => (
                      <Chip
                        key={index}
                        label={companyNum}
                        color="primary"
                        size="small"
                        icon={<VisibilityIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                Active subscriptions: {streamingStatus.active_subscriptions} |
                WebSocket connections: {streamingStatus.websocket_connections}
              </Typography>
            </Paper>
          )}

          {/* OAuth2 Section */}
          {oauthStatus && (
            <Paper sx={{ mt: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                🔐 OAuth2 Web Client
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    OAuth2 authentication enables access to advanced Secretary of State features
                    including address updates, officer management, and user-specific data.
                  </Typography>

                  {oauthStatus.oauth_configured ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      ✅ OAuth2 Web Client configured and ready
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      ⚠️ OAuth2 Web Client not configured
                    </Alert>
                  )}

                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Client ID: {oauthStatus.client_id} |
                    Active states: {oauthStatus.active_states} |
                    Authenticated users: {oauthStatus.authenticated_users}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={initiateOAuthFlow}
                    disabled={searchLoading || !oauthStatus.oauth_configured}
                    startIcon={<BusinessIcon />}
                    fullWidth
                  >
                    Authorize Access
                  </Button>
                </Grid>
              </Grid>

              {oauthStatus.supported_scopes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Permissions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {oauthStatus.supported_scopes.map((scope: string, index: number) => (
                      <Typography key={index} variant="caption" color="text.secondary">
                        • {scope.replace('https://api.companieshouse.gov.uk/', '').replace('https://api.sos.gov/', '').replace('/*', '/*')}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {companyProfile && (
            <Paper sx={{ mt: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Company Details</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Company Name" secondary={companyProfile.company_name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Company Number" secondary={companyProfile.company_number} />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Status"
                        secondary={
                          <Chip
                            label={companyProfile.company_status}
                            color={companyProfile.company_status.toLowerCase() === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Company Type" secondary={companyProfile.company_type} />
                    </ListItem>
                    {companyProfile.incorporation_date && (
                      <ListItem>
                        <ListItemText primary="Incorporation Date" secondary={companyProfile.incorporation_date} />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Registered Office</Typography>
                  <List dense>
                    {companyProfile.registered_office_address.address_line_1 && (
                      <ListItem>
                        <ListItemText secondary={companyProfile.registered_office_address.address_line_1} />
                      </ListItem>
                    )}
                    {companyProfile.registered_office_address.address_line_2 && (
                      <ListItem>
                        <ListItemText secondary={companyProfile.registered_office_address.address_line_2} />
                      </ListItem>
                    )}
                    {companyProfile.registered_office_address.locality && (
                      <ListItem>
                        <ListItemText secondary={companyProfile.registered_office_address.locality} />
                      </ListItem>
                    )}
                    {companyProfile.registered_office_address.postal_code && (
                      <ListItem>
                        <ListItemText secondary={companyProfile.registered_office_address.postal_code} />
                      </ListItem>
                    )}
                  </List>

                  {companyProfile.sic_codes.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>SIC Codes</Typography>
                      <Box sx={{ mt: 1 }}>
                        {companyProfile.sic_codes.map((code) => (
                          <Chip key={code} label={code} size="small" sx={{ mr: 1, mb: 1 }} />
                        ))}
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompaniesHouseIntegration;
